/**
 * Drawer registry — Tilleggsmoduler (C): drawer/topsheet components hosted by
 * components/footer/index.js that belong to products NOT in the Dyrejournal
 * build. The footer destructures from this map and renders unchanged — no
 * render-site guards needed because the export stub maps EVERY key to a
 * null-rendering component:
 *
 *   const Empty = () => null;
 *   export default new Proxy({}, { get: () => Empty });
 *
 * NOTE: the C-entity creators under components/admin/new* (newRace, newStream,
 * newConvention, ...) live in the B admin/ directory and are NOT moved here —
 * they are a deeper B→C coupling handled by the trial-excision check (2.10).
 * Classification source of truth: docs/dyrejournal-separation/manifest.md
 */
import TopSheet from '../components/topsheet/';
import NewsletterBuilder from '../components/newsletter/builder';
import NewsletterSendDrawer from '../components/newsletter/sendDrawer';

import BroadcastChannelEdit from '../components/tvRadioProducer/channelEdit';
import BroadcastEventEdit from '../components/tvRadioProducer/eventEdit';
import BroadcastEventView from '../components/tvRadioProducer/eventView';
import ChannelProgramList from '../components/tvRadioProducer/channelProgramList';
import TvRadioProducerHelp from '../components/tvRadioProducer/help';

import NewsView from '../components/news/view';
import NewsMenu from '../components/news/menu';
import NewsEdit from '../components/news/edit';
import NewsAdminList from '../components/news/listAdmin';

import PartView from '../components/part/view';
import PartEdit from '../components/part/edit';
import PartSort from '../components/part/sort';
import PartAddUsageData from '../components/part/addUsageData';
import PartAddToInstallation from '../components/part/addToInstallation';
import PartLoanToFriend from '../components/part/loanToFriend';
import PartEditLoan from '../components/part/editLoan';
import PartEditUsage from '../components/part/editUsage';
import PartViewUsage from '../components/part/viewUsage';
import PartViewLoan from '../components/part/viewLoan';
import PartDeleteFromIntallation from '../components/part/deleteFromInstallation';
import PartStatsDrawer from '../components/part/statsDrawer';
import PartAiLookup from '../components/part/aiLookup';
import PartPhotoSearch from '../components/part/photoSearch';

import PublicUserProfile from '../components/user/publicProfile';
import ArcadeCabinet from '../components/arcade/cabinet';
import AdventDoorDrawer from '../components/adventCalendar/doorDrawer';

import ArticleEdit from '../components/article/edit';
import BackgroundPresetsDrawer from '../components/article/backgroundPresetsDrawer';
import ArticleEditFactbox from '../components/article/editFactbox';
import ArticleView from '../components/article/view';
import ArticleList from '../components/article/list';
import ArticleAdminList from '../components/article/listAdmin';

import StreamEdit from '../components/streaming/edit';

import CoffeeList from '../components/coffee/list';
import CoffeeView from '../components/coffee/view';
import CoffeeDashboard from '../components/coffee/dashboard';
import CoffeeQuickRate from '../components/coffee/quickRate';

import LotTestingList from '../components/lotTesting/list';
import LotTestingView from '../components/lotTesting/view';
import LotTestingViewOnly from '../components/lotTesting/viewOnly';
import WeaponTestDrawer from '../components/lotTesting/weaponTestDrawer';
import AmmoSaleDrawer from '../components/ammoSale/drawer';
import WeaponCardDrawer from '../components/weaponCard/drawer';
import WeaponCardShow from '../components/weaponCard/show';
import ClubOpsMaintenanceDrawer from '../components/clubOps/maintenanceDrawer';
import ClubOpsKeyDrawer from '../components/clubOps/keyDrawer';
import ClubOpsLedgerDrawer from '../components/clubOps/ledgerDrawer';
import ClubOpsRangeInspectionDrawer from '../components/clubOps/rangeInspectionDrawer';

import EditExternalRaceDrawer from '../components/raceResultsBrowser/editExternalRaceDrawer';
import UploadExternalResultsDrawer from '../components/raceResultsBrowser/uploadExternalResultsDrawer';

import WorkSharingItemDetail from '../components/workSharing/itemDetail';
import WorkSharingNewItem from '../components/workSharing/newItem';

import TimeTrackerEdit from '../components/timetracker/edit';
import TimeTrackerView from '../components/timetracker/view';

import ConventionEdit from '../components/convention/edit';
import ConventionEditParticipant from '../components/convention/editParticipant';
import ConventionScoreTeamEditor from '../components/convention/scoreTeamEditor';
import NsfCompetitionWatchDrawer from '../components/nsfCompetition/watchDrawer';
import NsfCompetitionDetailDrawer from '../components/nsfCompetition/detailDrawer';
import NsfCompetitionInviteDrawer from '../components/nsfCompetition/inviteDrawer';
import ConventionShooterMarkEditor from '../components/convention/shooterMarkEditor';
import ConventionShootOffEditor from '../components/convention/shootOffEditor';
import ConventionEditStage from '../components/convention/editStage';
import ConventionBulkGenerateStages from '../components/convention/bulkGenerateStages';
import ConventionBulkGenerateConventions from '../components/convention/bulkGenerateConventions';
import ConventionWizard from '../components/convention/wizard';
import ConventionStageSelector from '../components/convention/stageSelector';
import ConventionView from '../components/convention/view';
import ConventionViewAdmin from '../components/convention/view-admin';
import ConventionViewOrganizersAdmin from '../components/convention/view-organizers-admin';
import ConventionViewTrainingSmsAdmin from '../components/convention/view-training-sms-admin';
import ConventionViewDugnadInviteAdmin from '../components/convention/view-dugnad-invite-admin';
import ConventionPaymentSummary from '../components/convention/paymentSummary';
import ConventionEconomy from '../components/convention/economy';
import ConventionCrewMessages from '../components/convention/crewMessages';
import ConventionStatsDrawer from '../components/convention/statsDrawer';
import ConventionPrizesAdmin from '../components/convention/prizesAdmin';
import ConventionStandplassCard from '../components/convention/standplassCard';
import ConventionCourseDrawer from '../components/convention/courseDrawer';
import ArticleStatsDrawer from '../components/article/statsDrawer';
import MarketplaceImportDrawer from '../components/marketplace/importDrawer';
import ShooterDetail from '../components/convention/shooterDetail';
import ShooterShots from '../components/convention/shooterShots';
import ConventionRankingShooterDetail from '../components/convention/rankingShooterDetail';
import ShooterCompare from '../components/convention/shooterCompare';

import ShotTrainerFieldRemote from '../components/shottrainer/fieldRemote';
import ShotTrainerFieldConsumer from '../components/shottrainer/fieldConsumer';

import ConventionResultEditImage from '../components/conventionresult/editImage';
import ConventionResultViewImage from '../components/conventionresult/viewImage';

import CourseEdit from '../components/course/edit';
import CourseView from '../components/course/view';
import CourseSendExam from '../components/course/sendExam';

import CompetitionResultEdit from '../components/competitionresult/edit';
import AiUploadCompetitionResult from '../components/competitionresult/aiUpload';

import RaceClassContestantAdminView from '../components/raceClassContestant/admin/view';
import RaceClassContestantAdminList from '../components/raceClassContestant/admin/list';
import RaceClassContestantView from '../components/raceClassContestant/view';
import GpsAnalysisDrawer from '../components/raceClassContestant/gpsAnalysisDrawer';
import RaceClassCheckpointView from '../components/raceClass/viewCheckpoint';
import RaceClassPaySignupFee from '../components/raceClassContestant/paySignupFee';
import RaceClassPaySignup from '../components/raceClassContestant/paySignup';
import RaceClassPayRest from '../components/raceClassContestant/payRest';
import RaceClassPayGeneric from '../components/raceClassContestant/payGeneric';
import RaceClassContestantCsvImport from '../components/raceClassContestant/importCsv';

import RaceViewMap from '../components/race/viewMap';
import RaceFileMapDrawer from '../components/race/fileMapDrawer';
import RaceViewResults from '../components/race/viewResults';
import RaceStatsDrawer from '../components/race/statsDrawer';
import RaceClassStatsDrawer from '../components/raceclass/statsDrawer';
import GeofenceDebugDrawer from '../components/race/geofenceDebugDrawer';
import GpsDeviceStatusDrawer from '../components/race/gpsDeviceStatusDrawer';
import RaceTrackerDayDrawer from '../components/race/trackerDayDrawer';
import RaceEditDrawer from '../components/race/editDrawer';
import RaceSignup from '../components/race/signup';

import EditInstallation from '../components/installation/edit';
import InspectInstallation from '../components/installation/inspect';
import EditInstallationPart from '../components/installation/editPart';
import DeleteInstallation from '../components/installation/delete';

import InspectionView from '../components/inspection/view';
import EditInspection from '../components/inspection/edit';

import MusherProfile from '../components/musherRating/profile';
import F1DriverInfoDrawer from '../components/f1/driverInfoDrawer';

// C-entity admin creators (live in the B components/admin/ dir; the files are
// excluded from the Dyrejournal export together with this registry)
import AdminNewNews from '../components/admin/newNews';
import AdminNewInstallation from '../components/admin/newInstallation';
import AdminNewPart from '../components/admin/newPart';
import AdminNewInspection from '../components/admin/newInspection';
import AdminNewLoan from '../components/admin/newLoan';
import AdminNewRace from '../components/admin/newRace';
import AdminNewRaceClass from '../components/admin/newRaceClass';
import AdminNewRaceClassContestant from '../components/admin/newRaceClassContestant';
import AddRaceClassContestant from '../components/raceClassContestant/addContestant';
import RaceClassEdit from '../components/raceClass/edit';
import RaceDuplicate from '../components/race/duplicateRace';
import RaceClassDuplicate from '../components/raceClass/duplicateRaceClass';
import AdminNewRaceResult from '../components/admin/newRaceResult';
import AdminNewTimeTracker from '../components/admin/newTimeTracker';
import AdminNewGpsDevice from '../components/admin/newGpsDevice';
import AdminNewGpsDevicesAvailable from '../components/admin/newGpsDevicesAvailable';
import AdminNewStream from '../components/admin/newStream';
import AdminNewStreamDevice from '../components/admin/newStreamDevice';
import AdminNewBroadcastChannel from '../components/admin/newBroadcastChannel';
import AdminNewBroadcastEvent from '../components/admin/newBroadcastEvent';
import AdminNewBroadcastDaySchedule from '../components/admin/newBroadcastDaySchedule';
import AdminNewBroadcastProgram from '../components/admin/newBroadcastProgram';
import AdminNewConvention from '../components/admin/newConvention';
import AdminNewConventionResult from '../components/admin/newConventionResult';
import AdminNewCompetitionResult from '../components/admin/newCompetitionResult';
import AdminNewCourse from '../components/admin/newCourse';
import AdminElearningLessonEdit from '../components/admin/elearning/lessonEdit';
import AdminViewGpsPosition from '../components/admin/viewGpsPosition';
// Shorts (feed video clips) — C; the footer renders this through the registry
// so the export stub turns the capture drawer into nothing (excision-gate cleanup 2026-10-21).
import ShortsCaptureDrawer from '../components/shorts/captureDrawer';

export default {
    AdminNewNews,
    AdminNewInstallation,
    AdminNewPart,
    AdminNewInspection,
    AdminNewLoan,
    AdminNewRace,
    AdminNewRaceClass,
    AdminNewRaceClassContestant,
    AddRaceClassContestant,
    RaceClassEdit,
    RaceDuplicate,
    RaceClassDuplicate,
    AdminNewRaceResult,
    AdminNewTimeTracker,
    AdminNewGpsDevice,
    AdminNewGpsDevicesAvailable,
    AdminNewStream,
    AdminNewStreamDevice,
    AdminNewBroadcastChannel,
    AdminNewBroadcastEvent,
    AdminNewBroadcastDaySchedule,
    AdminNewBroadcastProgram,
    AdminNewConvention,
    AdminNewConventionResult,
    AdminNewCompetitionResult,
    AdminNewCourse,
    AdminElearningLessonEdit,
    AdminViewGpsPosition,
    TopSheet,
    NewsletterBuilder,
    NewsletterSendDrawer,
    BroadcastChannelEdit,
    BroadcastEventEdit,
    BroadcastEventView,
    ChannelProgramList,
    TvRadioProducerHelp,
    NewsView,
    NewsMenu,
    NewsEdit,
    NewsAdminList,
    PartView,
    PartEdit,
    PartSort,
    PartAddUsageData,
    PartAddToInstallation,
    PartLoanToFriend,
    PartEditLoan,
    PartEditUsage,
    PartViewUsage,
    PartViewLoan,
    PartDeleteFromIntallation,
    PartStatsDrawer,
    PartAiLookup,
    PartPhotoSearch,
    ArticleEdit,
    BackgroundPresetsDrawer,
    ArticleEditFactbox,
    ArticleView,
    ArticleList,
    ArticleAdminList,
    StreamEdit,
    CoffeeList,
    CoffeeView,
    CoffeeDashboard,
    CoffeeQuickRate,
    LotTestingList,
    LotTestingView,
    LotTestingViewOnly,
    WeaponTestDrawer,
    AmmoSaleDrawer,
    WeaponCardDrawer,
    WeaponCardShow,
    ClubOpsMaintenanceDrawer,
    ClubOpsKeyDrawer,
    ClubOpsLedgerDrawer,
    ClubOpsRangeInspectionDrawer,
    EditExternalRaceDrawer,
    UploadExternalResultsDrawer,
    WorkSharingItemDetail,
    WorkSharingNewItem,
    TimeTrackerEdit,
    TimeTrackerView,
    ConventionEdit,
    ConventionEditParticipant,
    ConventionScoreTeamEditor,
    NsfCompetitionWatchDrawer,
    NsfCompetitionDetailDrawer,
    NsfCompetitionInviteDrawer,
    ConventionShooterMarkEditor,
    ConventionShootOffEditor,
    ConventionEditStage,
    ConventionBulkGenerateStages,
    ConventionBulkGenerateConventions,
    ConventionWizard,
    ConventionStageSelector,
    ConventionView,
    ConventionViewAdmin,
    ConventionViewOrganizersAdmin,
    ConventionViewTrainingSmsAdmin,
    ConventionViewDugnadInviteAdmin,
    ConventionPaymentSummary,
    ConventionEconomy,
    ConventionCrewMessages,
    ConventionStatsDrawer,
    ConventionPrizesAdmin,
    ConventionStandplassCard,
    ConventionCourseDrawer,
    ArticleStatsDrawer,
    MarketplaceImportDrawer,
    ShooterDetail,
    ShooterShots,
    ConventionRankingShooterDetail,
    ShooterCompare,
    ShotTrainerFieldRemote,
    ShotTrainerFieldConsumer,
    ConventionResultEditImage,
    ConventionResultViewImage,
    CourseEdit,
    CourseView,
    CourseSendExam,
    CompetitionResultEdit,
    AiUploadCompetitionResult,
    RaceClassContestantAdminView,
    RaceClassContestantAdminList,
    RaceClassContestantView,
    GpsAnalysisDrawer,
    RaceClassCheckpointView,
    RaceClassPaySignupFee,
    RaceClassPaySignup,
    RaceClassPayRest,
    RaceClassPayGeneric,
    RaceClassContestantCsvImport,
    RaceViewMap,
    RaceFileMapDrawer,
    RaceViewResults,
    RaceStatsDrawer,
    RaceClassStatsDrawer,
    GeofenceDebugDrawer,
    GpsDeviceStatusDrawer,
    RaceTrackerDayDrawer,
    RaceEditDrawer,
    RaceSignup,
    EditInstallation,
    InspectInstallation,
    EditInstallationPart,
    DeleteInstallation,
    InspectionView,
    EditInspection,
    MusherProfile,
    F1DriverInfoDrawer,
    ArcadeCabinet,
    AdventDoorDrawer,
    PublicUserProfile,
    ShortsCaptureDrawer,
};
